import type { StatusCode } from "@frontierx/types";

import { Button } from "@/components/ui/button";

import type { PartyServerConnectionStatus } from "../../stores/callStore";
import { useNetworkStatus } from "../NetworkStatus/NetworkStatusProvider";

export interface CallStatusProps {
  status?: StatusCode;
  handleRequestBotInfo?: () => void;
  partyServerConnectionStatus: PartyServerConnectionStatus;
  // Thrashing detection props
  isThrashing?: boolean;
  shouldSuggestRefresh?: boolean;
  thrashingMessage?: string;
  // Call completion detection
  hasCallEnded?: boolean;
}

export function CallStatus({
  status,
  handleRequestBotInfo,
  partyServerConnectionStatus,
  isThrashing = false,
  shouldSuggestRefresh = false,
  thrashingMessage = "",
  hasCallEnded = false,
}: Readonly<CallStatusProps>) {
  const { isOnline } = useNetworkStatus();

  const formatStatus = (status: string): string => {
    if (status === "in_call_recording") {
      return "In Call Recording";
    }
    if (status === "in_waiting_room") {
      return "In Waiting Room";
    }
    if (status === "joining_call") {
      return "Joining Call";
    }
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getStatusColor = (status: string): string => {
    // Green statuses - active/successful states
    if (
      [
        "call_connected",
        "in_call_recording",
        "audio_unmuted",
        "video_unmuted",
      ].includes(status)
    ) {
      return "bg-green-500";
    }

    // Orange statuses - transitional/warning states
    if (
      [
        "call_connecting",
        "joining_call",
        "in_waiting_room",
        "audio_muted",
        "video_muted",
        "in_call_not_recording",
        "reconnecting",
      ].includes(status)
    ) {
      return "bg-orange-500";
    }

    // Red statuses - error/failure states
    if (
      [
        "call_failed",
        "error",
        "call_disconnected",
        "disconnected",
        "offline",
      ].includes(status)
    ) {
      return "bg-red-500";
    }

    // Default gray for starting_up, ready, and unknown statuses
    return "bg-gray-500";
  };

  let displayStatus: string;
  let dotColor: string;

  if (!isOnline) {
    displayStatus = "Offline";
    dotColor = getStatusColor("offline");
  } else if (shouldSuggestRefresh && thrashingMessage) {
    // Prioritize thrashing message when suggesting refresh
    displayStatus = "Connection Issues";
    dotColor = getStatusColor("error");
  } else if (isThrashing) {
    // Show reconnecting with indication of multiple attempts
    displayStatus = "Reconnecting...";
    dotColor = getStatusColor("reconnecting");
  } else if (hasCallEnded) {
    // Show "Done" status when call has ended, regardless of raw Recall status
    displayStatus = "Call completed";
    dotColor = "bg-blue-500";
  } else if (status) {
    // Prioritize actual bot status when available
    displayStatus = formatStatus(status);
    dotColor = getStatusColor(status);
  } else if (
    partyServerConnectionStatus === "disconnected" ||
    partyServerConnectionStatus === "reconnecting"
  ) {
    // Fall back to websocket status only when no bot status is available
    displayStatus = formatStatus(partyServerConnectionStatus);
    dotColor = getStatusColor(partyServerConnectionStatus);
  } else {
    displayStatus = "Waiting";
    dotColor = "bg-gray-500";
  }

  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => handleRequestBotInfo?.()}
      data-test="call-status"
    >
      <div
        className={`h-2 w-2 rounded-full ${dotColor} mr-2`}
        data-test="call-status-indicator"
      />
      <span data-test="call-status-text">{displayStatus}</span>
    </Button>
  );
}
